import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import { makeStyles } from '@material-ui/core/styles';
import {
    Grid,
    Typography,
    Container,
    CircularProgress,
} from '@material-ui/core';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle, faQuestionCircle, faClock, faTimesCircle } from "@fortawesome/free-solid-svg-icons";
import { ItemDetailsBase, ListBase } from './index';
import { CredentialsService } from "@services";
import { useDispatch, useSelector } from 'react-redux';
import { formatMissingCert } from './index';

// redux functions
import { saveCertificateData } from "@store/certificates";
import { setCertificateTypes } from '@store/addCertificate';
import { Snackbar } from '@components/molecules';

const useStyles = makeStyles(theme => ({
    root: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
    },
    ledgerContainer: {
        paddingBottom: theme.spacing(1),
    },
    CURRENT: {
        color: theme.palette.icon.current,
    },
    EXPIRED: {
        color: theme.palette.icon.expired,
    },
    MISSING: {
        color: theme.palette.icon.missing,
    },
    PENDING: {
        color: theme.palette.icon.pending,
    },
    certificateContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        paddingBottom: theme.spacing(1),
    },
    ledgerTitle: {
        paddingLeft: theme.spacing(1),
    },
    center: {
        display: 'flex',
        justifyContent: 'center',
        position: 'absolute',
    },
}));

const ViewCertificates = (props) => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const history = useHistory();
    const { certificates } = useSelector(state => state.certificates);
    const { certificateTypes } = useSelector(state => state.addCertificate);
    const { user } = useSelector(state => state.user);
    const [certificateDetails, setCertificateDetails] = useState([]);
    const [isViewingCert, setIsViewingCert] = useState(false);
    const [selectedCert, setSelectedCert] = useState();
    const [currentlyViewing, setCurrentlyViewing] = useState();
    const [isLoading, setIsLoading] = useState(true);
    const [showPendingMessage, setShowPendingMessage] = useState(false);


    const listBaseRef = useRef(null);

    const handleItemDetails = (data, index) => {
        if (data.disposition === "PENDING") {
            setCurrentlyViewing();
            setIsViewingCert(false);
            setShowPendingMessage(true);
            return;
        }
        if (index === undefined) {
            setCurrentlyViewing(index);
            setIsViewingCert(true);
        } else if (index === currentlyViewing) {
            setCurrentlyViewing();
            setIsViewingCert(false);
        } else {
            setCurrentlyViewing(index);
            setIsViewingCert(true);
        }
        if (data) {
            setSelectedCert(data);
        }
    }

    const handleMobileBackButton = () => {
        setCurrentlyViewing();
        setIsViewingCert(false);
        setSelectedCert();
    }

    const renderItemDetails = () => {
        return (
            <ItemDetailsBase certificateDetails={selectedCert} handleBack={handleMobileBackButton} />
        )
    }

    const checkCertStatus = useCallback(() => {
        if (!(Array.isArray(certificates) && certificates.length)) return;
        let temp = [];
        for (let i = 0; i < certificates.length; i++) {
            const currentStatus = certificates[i].disposition;
            if (currentStatus === 'CURRENT') {
                temp.push(Object.assign({}, certificates[i], { iconName: faCheckCircle }));
            } else if (currentStatus === 'EXPIRED') {
                temp.push(Object.assign({}, certificates[i], { iconName: faTimesCircle }));
            } else if (currentStatus === 'MISSING') {
                if (certificates[i].certificateName !== null && certificates[i].certificateName.length > 30 && certificates[i].certificateName.includes('State')) {
                    const formatLabel = formatMissingCert(certificates[i], 'certificateName', ',');
                    temp.push(Object.assign({}, certificates[i], { iconName: faQuestionCircle }));
                    if (formatLabel) {
                        temp[temp.length - 1].certificateName = formatLabel;
                    }
                } else {
                    temp.push(Object.assign({}, certificates[i], { iconName: faQuestionCircle }));
                }
            } else if (currentStatus === 'PENDING') {
                temp.push(Object.assign({}, certificates[i], { iconName: faClock }));
            }
        }
        setCertificateDetails(temp);
    }, [certificates]);

    const getCertificates = useCallback(() => {
        setIsLoading(true);
        CredentialsService.getCerts(user?.employeeNumber)
            .then(certs => {
                dispatch(saveCertificateData(certs))
            })
            .then(() => {
                checkCertStatus();
                setIsLoading(false);
            })
            .catch(e => {
                setIsLoading(false);
                console.log(e);
            })
    }, [checkCertStatus, dispatch]);

    useEffect(() => {
        getCertificates();
    }, []);

    useEffect(() => {
        if (history?.location?.state && Array.isArray(certificateDetails) && certificateDetails.length) {
            const clickedCert = history?.location?.state;
            //to find the clicked cert
            let selectedCertIndex;
            const selectedCert = certificateDetails.find((cert, index) => {
                if ((cert.disposition === clickedCert.disposition || (['EXPIRING'].includes(clickedCert.disposition) && ['PENDING', 'CURRENT'].includes(cert.disposition))) &&
                    (cert.certificateName?.split(',').join(',') === clickedCert.certNameList?.split(' or ').join(', ') || (cert.stateCode && `${cert.stateCode}-${cert.certificateName}` === clickedCert.certNameList)) &&
                    (cert.stateCode || null) === clickedCert.stateCode &&
                    (cert.countyName || null) === clickedCert.countyName) {
                    selectedCertIndex = index;
                    if (typeof listBaseRef.current.getAllCertificateItems === 'function') {
                        const allCertificateRefs = listBaseRef.current.getAllCertificateItems();
                        allCertificateRefs[cert.certificateDispositionID].current.scrollIntoView({ behaviour: 'smooth', block: 'start' });
                    }

                    return cert;

                }
            });
            setSelectedCert(selectedCert);
            setCurrentlyViewing(selectedCertIndex);
            setIsViewingCert(true);
        }
    }, [certificateDetails]);

    useEffect(() => {
        checkCertStatus();
    }, [certificates]);

    const getCertificatesTypes = async () => {
        const certificateTypes = await CredentialsService.getCertificatesTypes();
        dispatch(setCertificateTypes(certificateTypes));
    };

    useEffect(() => {
        getCertificatesTypes();
    }, []);


    return (
        <div>
            {/* <Grid container flex className={classes.ledgerContainer}>
                <Grid item xs={6} md={3} className={classes.certificateContainer}>
                    <FontAwesomeIcon
                        icon={faCheckCircle}
                        size="lg"
                        className={classes.CURRENT}
                    />
                    <Typography variant="body2" color="textPrimary" className={classes.ledgerTitle}>
                        Current
                    </Typography>
                </Grid>
                <Grid item xs={6} md={3} className={classes.certificateContainer}>
                    <FontAwesomeIcon
                        icon={faTimesCircle}
                        size="lg"
                        className={classes.EXPIRED}
                    />
                    <Typography variant="body2" color="textPrimary" className={classes.ledgerTitle}>
                        Expired
                    </Typography>
                </Grid>
                <Grid item xs={6} md={3} className={classes.certificateContainer}>
                    <FontAwesomeIcon
                        icon={faQuestionCircle}
                        size="lg"
                        className={classes.MISSING}
                    />
                    <Typography variant="body2" color="textPrimary" className={classes.ledgerTitle}>
                        Missing
                    </Typography>
                </Grid>
                <Grid item xs={6} md={3} className={classes.certificateContainer}>
                    <FontAwesomeIcon
                        icon={faClock}
                        size="lg"
                        className={classes.PENDING}
                    />
                    <Typography variant="body2" color="textPrimary" className={classes.ledgerTitle}>
                        Pending
                    </Typography>
                </Grid>
            </Grid> */}

            <Container className={classes.root}>
                <div className={classes.center}>
                    {isLoading && (
                        <CircularProgress color="primary" size={75} />
                    )}
                </div>

                {certificateDetails && (
                    <ListBase
                        isViewingItem={isViewingCert}
                        handleOnClick={handleItemDetails}
                        certificateDetails={certificateDetails}
                        className={classes}
                        currentlyViewing={currentlyViewing}
                        ref={listBaseRef}
                        certificateTypes={certificateTypes}
                        isLoading={isLoading}
                    />
                )}

                {isViewingCert && selectedCert && (
                    renderItemDetails()
                )}
            </Container>

            <Snackbar
                open={showPendingMessage}
                alertMessage='This certification is pending approval by the certification manager and can not be updated.  Please contact your local certification manager or CES Dept for assistance'
                onClose={() => setShowPendingMessage(false)}
                alertProps={{ severity: 'info' }}
                autoHideDuration={2000000}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            />
        </div>
    );
};

export default ViewCertificates;
